import { filterClasses } from "@swivl-mono/helpers";
import React, { useCallback, useState } from "react";
import Cropper, { Area } from "react-easy-crop";

import { Button, ButtonColor, ButtonVariant } from "../Button/Button";
import { Modal, ModalSize } from "../Modal/Modal";

interface AvatarCropModalProps {
  imageSrc: string;
  isOpen: boolean;
  onClose: () => void;
  onCropComplete: (croppedFile: File) => void;
  round?: boolean;
  /** Crop aspect ratio (width / height). Defaults to 1 (square), for avatars. */
  aspect?: number;
  /** Modal heading — defaults to "Crop Avatar"; override for other image types (e.g. "Crop Logo"). */
  title?: string;
  /**
   * Shorter output edge in px (the other edge follows `aspect`). Defaults to
   * 300 (avatars). Brand logos pass 128 to match their recommended size.
   */
  outputSize?: number;
  /** Encoded output type. Defaults to JPEG; pass "image/png" to keep transparency (brand logos). */
  outputType?: "image/jpeg" | "image/png";
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.crossOrigin = "anonymous";
    image.src = url;
  });
}

async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  outputWidth = 300,
  outputHeight = 300,
  outputType: "image/jpeg" | "image/png" = "image/jpeg"
): Promise<File> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to get canvas context");

  // JPEG has no alpha, so paint white behind the crop; PNG keeps transparency.
  if (outputType === "image/jpeg") {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, outputWidth, outputHeight);
  }
  ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, outputWidth, outputHeight);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Canvas toBlob failed"));
        return;
      }
      const fileName = outputType === "image/png" ? "avatar.png" : "avatar.jpg";
      resolve(new File([blob], fileName, { type: outputType }));
    }, outputType);
  });
}

const AvatarCropModal = ({
  imageSrc,
  isOpen,
  onClose,
  onCropComplete,
  round = false,
  aspect = 1,
  title = "Crop Avatar",
  outputSize = 300,
  outputType = "image/jpeg"
}: AvatarCropModalProps): React.ReactNode => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const handleCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = useCallback(async () => {
    if (!croppedAreaPixels) return;
    // Keep the shorter edge at `outputSize` (300px for avatars) and derive the
    // other edge from the aspect ratio so wide crops keep resolution.
    const outputHeight = outputSize;
    const outputWidth = Math.round(outputHeight * aspect);
    const croppedFile = await getCroppedImg(imageSrc, croppedAreaPixels, outputWidth, outputHeight, outputType);
    onCropComplete(croppedFile);
  }, [croppedAreaPixels, imageSrc, onCropComplete, aspect, outputSize, outputType]);

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open) onClose();
    },
    [onClose]
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={ModalSize.MEDIUM} title={title}>
      <div className="flex flex-col gap-4 px-4 pb-2">
        <div className="relative h-[300px] w-full overflow-hidden rounded-sm">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape={round ? "round" : "rect"}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-grey-500">Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className={filterClasses(
              "h-1 w-full cursor-pointer appearance-none rounded-full bg-grey-200",
              "[&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-orange-500"
            )}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant={ButtonVariant.GHOST} color={ButtonColor.BLACK} onClick={onClose}>
            Cancel
          </Button>
          <Button variant={ButtonVariant.PRIMARY} color={ButtonColor.ORANGE} onClick={handleSave}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export { AvatarCropModal };
